import { notifyError } from "@/lib/notify-error";
import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { FormActions, PageHeader } from "@/components/layout";
import { useProject } from "@/components/projects";
import {
  getEmptyPageSelectionError,
  getPlanPageNumbers,
  ProjectDocumentPicker,
  type MultiSectionSelection,
  type PickerDocument,
  type SectionSelections,
  type SingleSectionSelection,
} from "@/components/project-document-picker";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn, revisionHasDocumentType } from "@/lib/utils";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { EditAccessGate } from "@/features/onboarding";

export const Route = createFileRoute("/fieldPilot/new")({
  component: NewFieldPilotPage,
});

type CreateMode = "takeoff" | "standalone";

function toPickerDocuments(documents: Array<{ _id: string; name: string; revisions: Array<{
  _id: string;
  revisionNumber: number;
  revisionName: string;
  effectiveDocumentType?: string;
  pageDocumentTypes?: string[];
  classificationStatus?: string;
}> }>, documentType: "schedule" | "plan_set"): PickerDocument[] {
  return documents
    .map((document) => ({
      _id: document._id,
      name: document.name,
      revisions: document.revisions
        .filter((revision) => revisionHasDocumentType(revision, documentType))
        .map((revision) => ({
          _id: revision._id,
          revisionNumber: revision.revisionNumber,
          revisionName: revision.revisionName,
          effectiveDocumentType: revision.effectiveDocumentType,
          classificationStatus: revision.classificationStatus,
        })),
    }))
    .filter((document) => document.revisions.length > 0);
}

function getStandaloneSelections(values: Record<string, SectionSelections>) {
  const schedule = values.schedule as SingleSectionSelection | undefined;
  const plans = values.plans as MultiSectionSelection | undefined;
  const planSelections = plans?.selections.filter((selection) => selection.revisionId) ?? [];
  return {
    scheduleRevisionId: schedule?.selection?.revisionId,
    planRevisionIds: planSelections.map((selection) => selection.revisionId),
    planPageNumbers: getPlanPageNumbers(planSelections),
    emptyPagesError: getEmptyPageSelectionError(planSelections),
  };
}

export function NewFieldPilotPage() {
  return (
    <EditAccessGate>
      <NewFieldPilotPageContent />
    </EditAccessGate>
  );
}

function NewFieldPilotPageContent() {
  const navigate = useAppNavigate();
  const { projectId, isLoading: isProjectLoading } = useProject();
  const [mode, setMode] = useState<CreateMode>("takeoff");
  const [name, setName] = useState("");
  const [takeoffJobId, setTakeoffJobId] = useState("");
  const [takeoffPickerOpen, setTakeoffPickerOpen] = useState(false);
  const [pickerValues, setPickerValues] = useState<Record<string, SectionSelections>>({
    schedule: { mode: "single", selection: null },
    plans: { mode: "multi", selections: [] },
  });

  const { data: documents = [], isPending: documentsPending } = useQuery({
    ...convexQuery(
      api.projectDocuments.listByProject,
      projectId ? { projectId: projectId as Id<"projects"> } : "skip"
    ),
    enabled: !!projectId,
  });
  const { data: takeoffJobs = [], isPending: takeoffPending } = useQuery(
    convexQuery(api.takeoffPilot.jobs.listAll, {})
  );
  const createStandalone = useMutation(api.fieldPilot.jobs.createStandaloneFromProjectRevisions);
  const createFromTakeoff = useMutation(api.fieldPilot.jobs.createFromTakeoffJob);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isProjectLoading && !projectId) {
      navigate({ to: "/project", title: "Projects" });
    }
  }, [isProjectLoading, projectId, navigate]);

  const scheduleDocuments = useMemo(() => toPickerDocuments(documents, "schedule"), [documents]);
  const planDocuments = useMemo(() => toPickerDocuments(documents, "plan_set"), [documents]);
  const projectTakeoffJobs = takeoffJobs.filter((job) => job.projectId === projectId);
  const selectedTakeoffJob = projectTakeoffJobs.find((job) => job._id === takeoffJobId);

  const handlePickerChange = (sectionKey: string, value: SectionSelections) => {
    setPickerValues((current) => ({ ...current, [sectionKey]: value }));
  };

  const handleSubmit = async () => {
    if (!projectId) return;
    setIsSubmitting(true);
    try {
      const trimmedName = name.trim();
      let jobId: Id<"fieldPilotJobs">;
      if (mode === "takeoff") {
        if (!selectedTakeoffJob) throw new Error("Select a TakeoffPilot job");
        jobId = await createFromTakeoff({
          takeoffJobId: selectedTakeoffJob._id as Id<"takeoffPilotJobs">,
          name: trimmedName || undefined,
        });
      } else {
        const { scheduleRevisionId, planRevisionIds, planPageNumbers, emptyPagesError } =
          getStandaloneSelections(pickerValues);
        if (!trimmedName) throw new Error("Name is required for standalone FieldPilot jobs");
        if (planRevisionIds.length === 0) throw new Error("Select at least one plan revision");
        if (emptyPagesError) throw new Error(emptyPagesError);
        jobId = await createStandalone({
          projectId: projectId as Id<"projects">,
          name: trimmedName,
          scheduleRevisionId: scheduleRevisionId as Id<"projectDocumentRevisions"> | undefined,
          planRevisionIds: planRevisionIds as Id<"projectDocumentRevisions">[],
          planPageNumbers,
        });
      }
      toast.success("FieldPilot job created");
      navigate({ to: "/fieldPilot/$jobId", params: { jobId }, title: "FieldPilot Job" });
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to create FieldPilot job");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isProjectLoading) return null;

  return (
    <div className="space-y-4">
      <PageHeader
        title="New FieldPilot Job"
        trail={[{ label: "FieldPilot", href: "/fieldPilot" }]}
        description="Track field status from a TakeoffPilot job or from a schedule and plan drawings."
      />

      <Card className="max-w-3xl">
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="fieldpilot-name">Job name</Label>
            <Input
              id="fieldpilot-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={mode === "takeoff" ? "Defaults to the TakeoffPilot job name" : "FieldPilot job name"}
            />
          </div>

          <Tabs value={mode} onValueChange={(value) => setMode(value as CreateMode)}>
            <TabsList>
              <TabsTrigger value="takeoff">From TakeoffPilot</TabsTrigger>
              <TabsTrigger value="standalone">Standalone</TabsTrigger>
            </TabsList>
            <TabsContent value="takeoff" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>TakeoffPilot job</Label>
                <Popover open={takeoffPickerOpen} onOpenChange={setTakeoffPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={takeoffPickerOpen}
                      disabled={takeoffPending}
                      className="w-full justify-between font-normal"
                    >
                      <span className="truncate">
                        {selectedTakeoffJob
                          ? `${selectedTakeoffJob.name} - ${selectedTakeoffJob.scheduleRevisionName ?? "No schedule"}`
                          : "Select a TakeoffPilot job"}
                      </span>
                      <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder="Search TakeoffPilot jobs..." />
                      <CommandList>
                        <CommandEmpty>No TakeoffPilot jobs found.</CommandEmpty>
                        <CommandGroup>
                          {projectTakeoffJobs.map((job) => {
                            const label = `${job.name} - ${job.scheduleRevisionName ?? "No schedule"}`;
                            return (
                              <CommandItem
                                key={job._id}
                                // Use the unique job id as the cmdk identity so
                                // jobs with identical labels never collide; the
                                // label drives search via keywords.
                                value={job._id}
                                keywords={[label]}
                                onSelect={() => {
                                  setTakeoffJobId(job._id);
                                  setTakeoffPickerOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "size-4",
                                    takeoffJobId === job._id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <span className="truncate">{label}</span>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-sm text-muted-foreground">
                  Included Takeoff tags, counts, and measurements are copied into FieldPilot as Not Installed.
                </p>
              </div>
            </TabsContent>
            <TabsContent value="standalone" className="pt-4">
              <ProjectDocumentPicker
                sections={[
                  {
                    key: "schedule",
                    label: "Schedule (optional)",
                    documents: scheduleDocuments,
                    selectionMode: "single",
                    revisionMode: "single",
                    addLabel: "Add Schedule",
                  },
                  {
                    key: "plans",
                    label: "Plan drawings",
                    documents: planDocuments,
                    selectionMode: "multi",
                    revisionMode: "single",
                    addLabel: "Add Plan Set",
                    pageKind: "plan_set",
                  },
                ]}
                values={pickerValues}
                onChange={handlePickerChange}
                isLoading={documentsPending}
                disabled={isSubmitting}
              />
            </TabsContent>
          </Tabs>

          <FormActions
            cancelTo="/fieldPilot"
            cancelTitle="FieldPilot"
            submitLabel="Create FieldPilot Job"
            submitting={isSubmitting}
            submitDisabled={!projectId}
            onSubmit={() => void handleSubmit()}
          />
        </CardContent>
      </Card>
    </div>
  );
}
